import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { SaveFullTextDocumentFunction } from '../StudyList';
import { SaveDocumentResponse } from 'components/FileUploadDialog';
import { OnReviewReferenceUpdated } from 'query/study/ReviewReference';

const defaultSaveFullTextDocument = async (
  referenceId: number,
  file: File,
  documentUrl: string
): Promise<SaveDocumentResponse> => {
  void referenceId;
  void file;
  void documentUrl;
  throw new Error('saveFullTextDocument is not implemented.');
};

export const defaultSaveFullTextLink: OnReviewReferenceUpdated = (): Promise<any> => {
  throw new Error('onFullTextLinkUpdated is not implemented.');
};

const StudyListContext = createContext({
  hasFullTextFeature: false,
  isHighlightsEnabled: false,
  saveFullTextDocument: defaultSaveFullTextDocument,
  onFullTextLinkUpdated: defaultSaveFullTextLink,
});

export const useStudyList = () => {
  const context = useContext(StudyListContext);
  if (context === undefined) {
    throw new Error('useStudyList must be used within a StudyListProvider');
  }
  return context;
};

type StudyListProviderProps = {
  isHighlightsEnabled: boolean;
  saveFullTextDocument?: SaveFullTextDocumentFunction;
  onFullTextLinkUpdated?: OnReviewReferenceUpdated;
};

export const StudyListProvider = ({
  children,
  isHighlightsEnabled,
  saveFullTextDocument,
  onFullTextLinkUpdated,
}: PropsWithChildren<StudyListProviderProps>) => {
  const hasFullTextFeature =
    typeof saveFullTextDocument === 'function' &&
    typeof onFullTextLinkUpdated === 'function';

  const memoizedSaveFullTextDocument = useCallback(
    saveFullTextDocument ?? defaultSaveFullTextDocument,
    [saveFullTextDocument]
  );

  const memoizedOnFullTextLinkUpdated = useCallback(
    onFullTextLinkUpdated ?? defaultSaveFullTextLink,
    [onFullTextLinkUpdated]
  );

  const contextValue = useMemo(
    () => ({
      isHighlightsEnabled,
      hasFullTextFeature,
      saveFullTextDocument: memoizedSaveFullTextDocument,
      onFullTextLinkUpdated: memoizedOnFullTextLinkUpdated,
    }),
    [
      isHighlightsEnabled,
      memoizedSaveFullTextDocument,
      memoizedOnFullTextLinkUpdated,
    ]
  );

  return (
    <StudyListContext.Provider value={contextValue}>
      {children}
    </StudyListContext.Provider>
  );
};
